<script>
import appConfig from '@src/app.config';

export default {
  page: {
    // All subcomponent titles will be injected into this template.
    titleTemplate(title) {
      title = typeof title === 'function' ? title(this.$store) : title;
      return title ? `${title} | ${appConfig.title}` : appConfig.title;
    },
  },
  data() {
    return {};
  },
};
</script>

<template>
  <div id="app">
    <!--
    Even when routes use the same component, treat them
    as distinct and create the component again.
    -->
    <RouterView :key="$route.fullPath" />
    <vue-progress-bar></vue-progress-bar>
  </div>
</template>

<!-- This should generally be the only global CSS in the app. -->
<style lang="scss">
// Allow element/type selectors, because this is global CSS.
// stylelint-disable selector-max-type, selector-class-pattern

// Normalize default styles across browsers,
// https://necolas.github.io/normalize.css/
@import '~normalize.css/normalize.css';

// Design variables and utilities from src/design.
@import '@design';
@import '~bootstrap/scss/bootstrap';
@import '~bootstrap-vue/dist/bootstrap-vue.css';
@import '~flatpickr/dist/flatpickr.css';
@import '~vue-multiselect/dist/vue-multiselect.min.css';
@import '~vuejs-dialog/dist/vuejs-dialog.min.css';

// #app {
//   min-height: 100vh;
// }

.cursor-pointer {
  cursor: pointer;
}

.p-2-5 {
  padding: 0.75rem !important;
}

.w-60 {
  width: 60px !important;
}

@media (max-width: 991.98px) {
  .w-60 {
    width: auto !important;
  }
}

.w-200 {
  width: 200px;
}

.w-150 {
  width: 150px;
}

.w-100 {
  width: 100px;
}

.w-300 {
  width: 300px;
}

.no-wrap {
  white-space: nowrap;
}

.toast-container {
  top: calc(57px + 1.5rem - 10px) !important;
  right: 16px !important;
}

.toasted {
  padding: 10px 20px !important;
}

.near-label-info {
  color: #ccc;
}

.flatpickr-time {
  input {
    font-size: 16px;
  }
}

.bg-header {
  background-color: $headerColor;
  border-color: $headerColor;
}

.bg-gray {
  background-color: $gray;
  border-color: $gray;
}

.border-gray {
  border: 1px solid #ced4da !important;
}

.border-muted {
  border: none;
}

.border-top-gray {
  border-top: 1px solid #ced4da !important;
}

.kks-multiselect-container-index {
  div.multiselect {
    padding: 0;
    min-height: 0;
  }

  div.multiselect__tags {
    height: calc(1.5em + 0.5rem);
    min-height: 0;
    padding: 0.2rem 0.5rem 0rem;
    font-size: 0.875rem !important;
    line-height: 1.5;
    border-radius: 0;
    border-top-right-radius: 0px;
    border-bottom-right-radius: 0px;
    margin-top: 0px;
    border: none;
  }

  div.multiselect__tags > input {
    padding: 0 !important;
    margin-top: 0px;
    margin-bottom: 0;
  }

  .multiselect__placeholder,
  .multiselect__content-wrapper {
    font-size: 0.875rem !important;
    color: #6c757d;
    padding-top: 0px;
    padding-bottom: 2px;
  }

  input.multiselect__input {
    font-size: 0.875rem !important;
  }

  div.multiselect__select {
    padding: 0px 8px;
    height: 28px;
  }

  span.multiselect__tag {
    margin-top: 0px !important;
    margin-bottom: 0px !important;
    margin-right: 5px !important;
  }
}

.kks-multiselect-container {
  div.multiselect__tags {
    min-height: 38px;
    padding: 3px 40px 0 0.75rem;
    color: #495057;
    border: 1px solid #ced4da;
    border-radius: 0;
    -webkit-transition:
      border-color 0.15s ease-in-out,
      -webkit-box-shadow 0.15s ease-in-out;
    transition:
      border-color 0.15s ease-in-out,
      -webkit-box-shadow 0.15s ease-in-out;
    transition:
      border-color 0.15s ease-in-out,
      box-shadow 0.15s ease-in-out;
    transition:
      border-color 0.15s ease-in-out,
      box-shadow 0.15s ease-in-out,
      -webkit-box-shadow 0.15s ease-in-out;
  }

  div.multiselect__tags > input {
    padding: 0;
    margin-top: 4px;
    margin-bottom: 0;
  }

  .multiselect__placeholder {
    margin-bottom: 0;
    font-size: 1rem;
    color: #6c757d;
  }

  .multiselect__single {
    padding-left: 0;
    margin-top: 4px;
  }

  .multiselect--active {
    .multiselect__input {
      width: 100% !important;
    }
  }
}

.multiselect-container {
  &.is-invalid {
    div.multiselect__tags {
      border-color: $danger;
    }
  }

  &.focused {
    div.multiselect__tags {
      border-color: #5bc6e5;
      outline: 0;
      -webkit-box-shadow: 0 0 0 0.2rem rgba(26, 135, 167, 0.25);
      box-shadow: 0 0 0 0.2rem rgba(26, 135, 167, 0.25);
    }

    &.is-invalid {
      div.multiselect__tags {
        border-color: $danger;
        -webkit-box-shadow: 0 0 0 0.2rem rgba(226, 74, 65, 0.25);
        box-shadow: 0 0 0 0.2rem rgba(226, 74, 65, 0.25);
      }
    }
  }
}


// toggle buttons
.btn-group-toggle label {
  color: $dark !important;
}

.btn-group-toggle label:hover {
  color: $light !important;
}

.btn-group-toggle label.active {
  color: $light !important;
}

// breadcrumbs
.router-link-active {
  color: $breadcrumb-active-color;
}

.router-link-active:hover {
  color: darken($breadcrumb-active-color, 50%);
}

// pagination
.pagination {
  color: $gray !important;
}

.page-item.active .page-link {
  color: $light !important;
  background-color: $gray !important;
  border: none !important;
}

.page-item .page-link {
  color: $gray !important;
}

// dropdown
.dropdown-item:active {
  background-color: $dark !important;
}

//buttons
.btn-tabs.btn-dark {
  color: $light !important;
}

.btn-tabs.btn-light {
  color: $dark !important;
  background-color: white !important;
  border: 1px solid $dark !important;
}

.clear-calendar {
  position: absolute;
  top: 0;
  right: 24px;
  font-size: 24px;
  cursor: pointer;
}

.pointer {
  cursor: pointer;
}

.custom-file-label {
  overflow-x: hidden;
  color: $placeholder-gray;
  white-space: nowrap;
}

.dg-main-content {
  max-width: 470px;

  .dg-btn {
    @extend .btn;
  }

  .dg-btn--cancel {
    @extend .btn-link;
  }

  .dg-btn--ok {
    @extend .btn-primary;
  }

  .dg-content {
    @extend .lead;
  }

  .dg-title {
    @extend .h4;
  }

  @media (max-width: map_get($grid-breakpoints, 'md')) {
    .dg-btn {
      display: block;
      width: 100%;
    }
  }
}

.status-star {
  font-size: $h3-font-size;
  font-weight: bold;
  line-height: 0;
}

div.is-invalid {
  .flatpickr-input {
    @extend .is-invalid;
  }
}

.bg-info-light {
  background-color: lighten($info, 55%);
}

.bg-secondary-light {
  background-color: lighten($secondary, 50%);
}

.bg-success-light {
  background-color: lighten($success, 55%);
}

.w-20 {
  min-width: 20px !important;
  width: 20px !important;
  max-width: 20px !important;
}

.w-30 {
    min-width: 30px !important;
    width: 30px !important;
    max-width: 30px !important;
}

.w-40 {
  min-width: 40px !important;
  width: 40px !important;
  max-width: 40px !important;
}

.h-30 {
  min-height: 30px !important;
  height: 30px !important;
  max-height: 30px !important;
}

.w-35 {
  min-width: 35px !important;
  width: 35px !important;
  max-width: 35px !important;
}

.h-35 {
  min-height: 35px !important;
  height: 35px !important;
  max-height: 35px !important;
}

@media (max-width: 767.98px) {
  .w-40 {
    max-width: 100% !important;
  }
}

@media (max-width: 991.98px) {
  .w-40 {
    max-width: 100% !important;
    min-width: 100% !important;
    width: 100% !important;
  }
}

/** ACTION BUTTON FIXES */
td[data-label="Actions"] .btn {
    max-width: 34px !important;
    min-width: 34px !important;
    width: 34px !important;
    min-height: 34px !important;
    height: 34px !important;
    max-height: 34px !important;
}

/**
 * TABLE FIXES
 */

// table font fixes
.table.b-table {
  font-size: 0.75rem;
}

// sort icon fix
/* Hide default icon */
.table.b-table > thead > tr > th,
.table.b-table > tfoot > tr > td {
  background-image: none !important;
}

// sort label min width
.table.b-table.table-sm > thead > tr > [aria-sort]:not(.b-table-sort-icon-left),
.table.b-table.table-sm > tfoot > tr > [aria-sort]:not(.b-table-sort-icon-left) {
  min-width: 100px;
}

.table.b-table.no-min-width.table-sm > thead > tr > [aria-sort]:not(.b-table-sort-icon-left),
.table.b-table.table-sm > tfoot > tr > [aria-sort]:not(.b-table-sort-icon-left) {
  min-width: auto !important;
}
</style>
